// Instances section: this instance's identity and the other instances users
// can be routed to at login (dev, test, a client's prod VM). A user's
// "Instance" (user page → Access) picks one. Access overview: an instance can
// let the central one read who uses which assistant and data set there (no
// client data) — a token created there, entered here (access_overview.py).
// The "Who sees which data" map then shows routed users' full path.

import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faFloppyDisk, faTrash, faPen, faArrowsRotate, faXmark, faCircleInfo, faServer, faDiagramProject, faKey, faCopy,
} from '@fortawesome/free-solid-svg-icons';
import { Field, ConfirmDialog, Spinner, PasswordInput } from './ui';
import { useConsole } from './context';

const ENV_LABEL = { dev: 'Dev', test: 'Test', prod: 'Prod' };
const EMPTY = { key: '', label: '', url: '', environment: 'prod', client: '', description: '' };

function EnvBadge({ env }) {
  return env ? <span className={`um-env um-env-${env}`}>{ENV_LABEL[env] || env}</span> : null;
}

function InstanceForm({ initial, isNew, onCancel, onSaved, flash }) {
  const { client } = useConsole();
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [token, setToken] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await client.post('/api/admin/instances', form);
      else await client.put(`/api/admin/instances/${initial.key}`, form);
      if (token) await client.put(`/api/admin/instances/${form.key}/overview-token`, { token });
      flash('success', `Instance ${form.label} saved.`);
      onSaved();
    } catch (e) {
      setErrors(Object.fromEntries((e.fields || []).map((f) => [f, e.message])));
      flash('error', e.message);
    }
    setSaving(false);
  };
  return (
    <div className="um-inline-form">
      <div className="um-grid-2">
        <Field label="Label" error={errors.label}><input value={form.label} onChange={(e) => set('label', e.target.value)} placeholder="Dev (Marcuard Heritage)" /></Field>
        <Field label="Key" error={errors.key} hint={isNew ? 'Stored on routed users — cannot be changed later.' : ''}>
          <input className="um-mono" value={form.key} disabled={!isNew} onChange={(e) => set('key', e.target.value.toLowerCase())} placeholder="dev" />
        </Field>
        <Field label="URL" wide error={errors.url} hint="The instance's public address; routed users' browsers talk to it directly.">
          <input value={form.url} onChange={(e) => set('url', e.target.value)} placeholder="https://dev-heybro-business.integraal-partners.ch" />
        </Field>
        <Field label="Environment" error={errors.environment}>
          <select value={form.environment || ''} onChange={(e) => set('environment', e.target.value)}>
            <option value="">—</option><option value="dev">Dev</option><option value="test">Test</option><option value="prod">Prod</option>
          </select>
        </Field>
        <Field label="Client" error={errors.client}><input value={form.client || ''} onChange={(e) => set('client', e.target.value)} placeholder="Marcuard Heritage" /></Field>
        <Field label="Description" wide><input value={form.description || ''} onChange={(e) => set('description', e.target.value)} /></Field>
        <Field label="Access overview token" wide
          hint={initial.has_overview_token
            ? 'Stored (encrypted). Enter a new one to replace it.'
            : 'Created on that instance (Instances → Access overview). Lets the map here show what its users use there.'}>
          <PasswordInput value={token} onChange={setToken} placeholder={initial.has_overview_token ? '•••••••• stored' : 'Paste the token'} autoComplete="off" />
        </Field>
      </div>
      <div className="um-dialog-btns">
        <button type="button" className="um-btn um-btn-ghost" onClick={onCancel}><FontAwesomeIcon icon={faXmark} /> Cancel</button>
        <button type="button" className="um-btn um-btn-primary" onClick={save} disabled={saving}><FontAwesomeIcon icon={faFloppyDisk} /> {saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
}

export default function InstancesSection({ flash, overview, onSettingsSaved }) {
  const { client, meta } = useConsole();
  const canEdit = meta.permissions.manage_instance;
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);       // key | 'new'
  const [confirm, setConfirm] = useState(null);
  const [settings, setSettings] = useState(overview?.settings || {});
  const [savingSettings, setSavingSettings] = useState(false);
  const [shared, setShared] = useState(null);           // { configured, token? }

  useEffect(() => { setSettings(overview?.settings || {}); }, [overview]);

  const load = useCallback(async () => {
    setError('');
    try { setList(await client.get('/api/admin/instances')); } catch (e) { setError(e.message); }
    client.get('/api/admin/overview-token').then(setShared).catch(() => setShared(null));
  }, [client]);

  const createToken = async () => {
    try { setShared(await client.post('/api/admin/overview-token', {})); } catch (e) { flash('error', e.message); }
  };
  const revokeToken = async () => {
    setConfirm(null);
    try { setShared(await client.del('/api/admin/overview-token')); flash('success', 'Access overview token revoked.'); } catch (e) { flash('error', e.message); }
  };
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await client.put('/api/admin/settings', settings);
      flash('success', 'Instance settings saved.');
      onSettingsSaved?.();
    } catch (e) { flash('error', e.message); }
    setSavingSettings(false);
  };

  const remove = async (key) => {
    setConfirm(null);
    try { await client.del(`/api/admin/instances/${key}`); flash('success', 'Instance removed.'); load(); } catch (e) { flash('error', e.message); }
  };

  return (
    <>
      <div className="um-section-bar">
        <div className="um-subtitle">This instance, and the other instances users can be routed to when they log in here</div>
        <div className="um-topbar-right">
          <button type="button" className="um-icon-btn" onClick={load} title="Reload"><FontAwesomeIcon icon={faArrowsRotate} /></button>
        </div>
      </div>

      <div className="um-panel">
        <section className="um-block">
          <h4 className="um-block-title"><FontAwesomeIcon icon={faServer} />&nbsp; This instance</h4>
          <div className="um-grid-2">
            <Field label="Name" hint="Shown in the console header and the routing choices.">
              <input value={settings.instance_label || ''} disabled={!canEdit} onChange={(e) => setSettings((s) => ({ ...s, instance_label: e.target.value }))} placeholder="Prod · survista" />
            </Field>
            <Field label="Environment">
              <select value={settings.instance_environment || ''} disabled={!canEdit} onChange={(e) => setSettings((s) => ({ ...s, instance_environment: e.target.value }))}>
                <option value="">—</option><option value="dev">Dev</option><option value="test">Test</option><option value="prod">Prod</option>
              </select>
            </Field>
            <Field label="Client"><input value={settings.instance_client || ''} disabled={!canEdit} onChange={(e) => setSettings((s) => ({ ...s, instance_client: e.target.value }))} placeholder="survista" /></Field>
          </div>
          {canEdit && (
            <div className="um-dialog-btns"><button type="button" className="um-btn um-btn-primary" onClick={saveSettings} disabled={savingSettings}><FontAwesomeIcon icon={faFloppyDisk} /> Save</button></div>
          )}
        </section>

        <section className="um-block">
          <h4 className="um-block-title"><FontAwesomeIcon icon={faDiagramProject} />&nbsp; Access overview</h4>
          <div className="um-hint">
            Lets the central instance show, in its &quot;Who sees which data&quot; map, which assistant and data set people use
            here. Only names leave this instance — no client data, no filter values.
          </div>
          {shared?.token ? (
            <div className="um-secret-stored">
              <FontAwesomeIcon icon={faKey} /> <code className="um-token">{shared.token}</code>
              <button type="button" className="um-link-btn" onClick={() => navigator.clipboard?.writeText(shared.token)}><FontAwesomeIcon icon={faCopy} /> Copy</button>
              <span className="um-muted">Shown once — enter it at the central instance (Instances → this instance → Access overview token).</span>
            </div>
          ) : (
            <div className="um-secret-stored">
              <FontAwesomeIcon icon={faKey} /> {shared?.configured ? 'Shared — a token exists.' : 'Not shared.'}
              {canEdit && (
                <span className="um-inline-actions">
                  <button type="button" className="um-link-btn" onClick={createToken}>{shared?.configured ? 'Create a new token' : 'Create token'}</button>
                  {shared?.configured && <button type="button" className="um-link-btn" onClick={() => setConfirm('revoke-token')}>Revoke</button>}
                </span>
              )}
            </div>
          )}
        </section>

        <section className="um-block">
          <div className="um-block-head">
            <h4 className="um-block-title">Routing targets</h4>
            {canEdit && editing !== 'new' && (
              <button type="button" className="um-btn um-btn-ghost" onClick={() => setEditing('new')}><FontAwesomeIcon icon={faPlus} /> Add instance</button>
            )}
          </div>
          <div className="um-hint">A user whose &quot;Instance&quot; (user page → Access) points to one of these logs in at this address and is handed over; their browser then works with that instance directly.</div>
          {editing === 'new' && <InstanceForm isNew initial={EMPTY} flash={flash} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
          {error && <div className="um-hint error">{error}</div>}
          {!list ? <div className="um-loading"><Spinner /></div> : list.length === 0 && editing !== 'new' ? (
            <div className="um-empty-card"><FontAwesomeIcon icon={faCircleInfo} /><div>No other instances registered. Everyone logs in and works here.</div></div>
          ) : (
            <div className="um-instance-list">
              {list.map((i) => (editing === i.key ? (
                <InstanceForm key={i.key} initial={i} flash={flash} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
              ) : (
                <div key={i.key} className="um-instance">
                  <div className="um-instance-main">
                    <div className="um-instance-title"><strong>{i.label}</strong> <EnvBadge env={i.environment} /> <code>{i.key}</code></div>
                    <a href={i.url} target="_blank" rel="noreferrer" className="um-instance-url">{i.url}</a>
                    <div className="um-hint">{[i.client, i.description].filter(Boolean).join(' · ')}</div>
                  </div>
                  <div className="um-instance-users">
                    {i.users.length ? <>{i.users.length} user{i.users.length === 1 ? '' : 's'}: {i.users.join(', ')}</> : <span className="um-muted">no users routed</span>}
                    <div className={i.has_overview_token ? 'um-ok-text' : 'um-muted'}>
                      Access overview: {i.has_overview_token ? 'connected' : 'not connected'}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="um-inline-actions">
                      <button type="button" className="um-icon-btn" title="Edit" onClick={() => setEditing(i.key)}><FontAwesomeIcon icon={faPen} /></button>
                      <button type="button" className="um-icon-btn danger" title="Remove" onClick={() => setConfirm(i.key)}><FontAwesomeIcon icon={faTrash} /></button>
                    </div>
                  )}
                </div>
              )))}
            </div>
          )}
        </section>
      </div>

      {confirm === 'revoke-token' && (
        <ConfirmDialog title="Revoke the access overview token?" text="The central instance's map then shows only the hand-over line for users of this instance."
          confirmLabel="Revoke" danger onConfirm={revokeToken} onCancel={() => setConfirm(null)} />
      )}
      {confirm && confirm !== 'revoke-token' && (
        <ConfirmDialog title="Remove this instance?" text="Only possible while no user is routed there." confirmLabel="Remove"
          danger onConfirm={() => remove(confirm)} onCancel={() => setConfirm(null)} />
      )}
    </>
  );
}
